import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface TaxExemption {
  id: string;
  organization_id: string;
  state_code: string;
  exemption_type: 'resale' | 'nonprofit' | 'government' | 'manufacturing' | 'agricultural' | 'other';
  certificate_number: string | null;
  issuing_state: string | null;
  issue_date: string | null;
  expiry_date: string | null;
  document_url: string;
  verification_status: 'pending' | 'verified' | 'rejected' | 'expired';
  verified_by_user_id: string | null;
  verified_at: string | null;
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface TaxExemptionWithOrg extends TaxExemption {
  organizations?: {
    name: string;
  };
}

// Fetch exemptions for an organization
export function useTaxExemptions(organizationId?: string) {
  return useQuery({
    queryKey: ['tax-exemptions', organizationId],
    queryFn: async () => {
      if (!organizationId) return [];
      
      const { data, error } = await supabase
        .from('organization_tax_exemptions')
        .select('*')
        .eq('organization_id', organizationId)
        .order('state_code', { ascending: true });

      if (error) throw error;
      return data as TaxExemption[];
    },
    enabled: !!organizationId,
  });
}

// Fetch all pending exemptions for admin review
export function usePendingTaxExemptions() {
  return useQuery({
    queryKey: ['tax-exemptions', 'pending'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organization_tax_exemptions')
        .select(`
          *,
          organizations:organization_id (name)
        `)
        .eq('verification_status', 'pending')
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data as TaxExemptionWithOrg[];
    },
  });
}

// Fetch all exemptions for admin (with optional status filter)
export function useAllTaxExemptions(status?: string) {
  return useQuery({
    queryKey: ['tax-exemptions', 'all', status],
    queryFn: async () => {
      let query = supabase
        .from('organization_tax_exemptions')
        .select(`
          *,
          organizations:organization_id (name)
        `)
        .order('created_at', { ascending: false });

      if (status && status !== 'all') {
        query = query.eq('verification_status', status);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as TaxExemptionWithOrg[];
    },
  });
}

// Create a new tax exemption
export function useCreateTaxExemption() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (exemption: {
      organization_id: string;
      state_code: string;
      exemption_type: string;
      certificate_number?: string;
      issuing_state?: string;
      issue_date?: string;
      expiry_date?: string;
      document_url: string;
    }) => {
      const { data, error } = await supabase
        .from('organization_tax_exemptions')
        .insert(exemption)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tax-exemptions', variables.organization_id] });
      queryClient.invalidateQueries({ queryKey: ['tax-exemptions', 'pending'] });
      toast({
        title: 'Certificate submitted',
        description: 'Your tax exemption certificate has been submitted for review.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to submit certificate',
        variant: 'destructive',
      });
    },
  });
}

// Update exemption (for admin verification)
export function useUpdateTaxExemption() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ 
      id, 
      ...updates 
    }: { 
      id: string;
      verification_status?: string;
      rejection_reason?: string;
      verified_by_user_id?: string;
      verified_at?: string;
    }) => {
      const { data, error } = await supabase
        .from('organization_tax_exemptions')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tax-exemptions'] });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update exemption',
        variant: 'destructive',
      });
    },
  });
}

// Verify exemption (admin action)
export function useVerifyTaxExemption() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, userId }: { id: string; userId: string }) => {
      const { data, error } = await supabase
        .from('organization_tax_exemptions')
        .update({
          verification_status: 'verified',
          verified_by_user_id: userId,
          verified_at: new Date().toISOString(),
          rejection_reason: null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tax-exemptions'] });
      toast({
        title: 'Certificate verified',
        description: 'The tax exemption certificate has been verified.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to verify certificate',
        variant: 'destructive',
      });
    },
  });
}

// Reject exemption (admin action)
export function useRejectTaxExemption() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const { data, error } = await supabase
        .from('organization_tax_exemptions')
        .update({
          verification_status: 'rejected',
          rejection_reason: reason,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tax-exemptions'] });
      toast({
        title: 'Certificate rejected',
        description: 'The tax exemption certificate has been rejected.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to reject certificate',
        variant: 'destructive',
      });
    },
  });
}

// Delete exemption
export function useDeleteTaxExemption() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('organization_tax_exemptions')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tax-exemptions'] });
      toast({
        title: 'Certificate deleted',
        description: 'The tax exemption certificate has been removed.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete certificate',
        variant: 'destructive',
      });
    },
  });
}

// US State options for exemption forms
export const US_STATES = [
  { code: 'ALL', name: 'All States (Blanket Exemption)' },
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
  { code: 'DC', name: 'District of Columbia' },
];

export const EXEMPTION_TYPES = [
  { value: 'resale', label: 'Resale Certificate', description: 'Buying for resale to end customers' },
  { value: 'nonprofit', label: 'Nonprofit Organization', description: '501(c)(3) or similar tax-exempt status' },
  { value: 'government', label: 'Government Entity', description: 'Federal, state, or local government' },
  { value: 'manufacturing', label: 'Manufacturing Exemption', description: 'Materials used in manufacturing' },
  { value: 'agricultural', label: 'Agricultural Exemption', description: 'Agricultural products or equipment' },
  { value: 'other', label: 'Other', description: 'Other type of exemption' },
];
